import React, { ChangeEvent, FC, useRef, useState, useEffect, DragEvent } from 'react';
import { Button, Link, Text, Types } from '@marriott/mi-ui-library';
import { ImageType } from '@marriott/mi-groups-graphql';
import { ImageCardProps } from './ImageCard.types';
import { StyledImageCard, StyledImageCardShell } from './ImageCard.styles';
import { RESLINK_ALLOWED_IMAGE_TYPES, RESLINK_IMAGE_MAX_SIZE } from '../../../../../constants';
import { uploadFile, deleteFile, getPropertyImageUrlParams } from '../../../../../../utils';
import { UploadErrorResponse } from '../../../../../../molecules/Attachments/Attachments.types';
import { ERROR_CODES, PROPERTY_IMAGE_DIMENSIONS } from '../../../../../../constants';
import { ImageCropper } from '../../../../../molecules';
import { useReslinkAssociateStore } from '../../../../../store';
import { PreApprovedImages } from '../PreApprovedImages';
import { PreApprovedImage } from '../PreApprovedImages/PreApprovedImages.types';

export const ImageCard: FC<ImageCardProps> = ({
  index,
  fields,
  append,
  remove,
  placeholder,
  chooseImage,
  dragHere,
  removeAriaLabel,
  imageSizeError,
  imageFormatError,
  virusImageApiError,
  uploadImageApiError,
  removeImageApiError,
  or,
  addHotelToSeeImages,
  chooseFromHotelImages,
  crop,
  cropAriaLabel,
  imageCropperPopUp,
  approvedImagesPopUp,
  image: imageData,
  onError,
  enableImageCropper,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState<ImageType | null>(null);
  const [imageKey, setImageKey] = useState<string>('');
  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const [deleteFileLoader, setDeleteFileLoader] = useState(false);

  const [showCropper, setShowCropper] = useState(false);
  const [showPreApprovedImages, setShowPreApprovedImages] = useState(false);
  const [isPreApprovedImage, setIsPreApprovedImage] = useState(false);

  const { preApprovedPropertyImages } = useReslinkAssociateStore();

  useEffect(() => {
    if (imageData?.link) {
      setImage({ name: imageData.name || '', link: imageData.link });
      setImageKey(imageData.link);
    }
  }, [imageData]);

  const selectImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const validateFile = (file: File): boolean => {
    if (file.size >= RESLINK_IMAGE_MAX_SIZE) {
      onError(imageSizeError);
      return false;
    }
    if (!RESLINK_ALLOWED_IMAGE_TYPES.includes(file.type)) {
      onError(imageFormatError);
      return false;
    }
    return true;
  };

  const uploadImage = async (file: File | undefined) => {
    if (file && validateFile(file)) {
      setUploadFileLoader(true);
      setIsPreApprovedImage(false);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      try {
        const response = await uploadFile(file, 'reslink');

        if (!response.ok) {
          const errorResponse = await response.json();
          throw errorResponse;
        }

        const result = await response.json();
        const { data } = result;
        append({ name: file.name, link: data?.[0].documentId || '' });
        const uploadedImage = data?.[0];
        if (uploadedImage) {
          setImage({ name: file.name, link: reader?.result as string });
          setImageKey(data?.[0].documentId);
          onError('');
        }
      } catch (error) {
        const { errors, status } = error as UploadErrorResponse;
        const errorCode = errors?.[0].code;
        if (status === ERROR_CODES.HTTP.UNPROCESSABLE_ENTITY && errorCode === ERROR_CODES.IMAGE_UPLOAD.SIZE) {
          onError(imageSizeError);
        } else if (status === ERROR_CODES.HTTP.UNPROCESSABLE_ENTITY && errorCode === ERROR_CODES.IMAGE_UPLOAD.FORMAT) {
          onError(imageFormatError);
        } else if (status === ERROR_CODES.HTTP.UNPROCESSABLE_ENTITY && errorCode === ERROR_CODES.IMAGE_UPLOAD.VIRUS) {
          onError(virusImageApiError);
        } else {
          onError(uploadImageApiError);
        }
      } finally {
        setUploadFileLoader(false);
      }
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    uploadImage(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (image?.link) {
      return;
    }

    const file = event.dataTransfer.files?.[0];
    uploadImage(file);
  };

  const cleanupImageState = (imageIndex: number) => {
    remove(imageIndex);
    setImage(null);
    onError('');
  };

  const handleImageDeletion = async () => {
    const imageIndex = fields.findIndex(field => field.link === imageKey);

    if (isPreApprovedImage) {
      cleanupImageState(imageIndex);
      setIsPreApprovedImage(false);
      return true;
    } else {
      setDeleteFileLoader(true);
      try {
        const response = await deleteFile(imageKey, 'reslink');

        if (!response.ok) {
          const errorResponse = await response.json();
          throw errorResponse;
        }

        const result = await response.json();
        if (result.data.fileDetails?.[0]) {
          cleanupImageState(imageIndex);
          return true;
        } else {
          onError(removeImageApiError);
          return false;
        }
      } catch (error) {
        onError(removeImageApiError);
        return false;
      } finally {
        setDeleteFileLoader(false);
      }
    }
  };

  const uploadCroppedImage = async (newFile: File) => {
    const deleted = await handleImageDeletion();
    if (deleted) {
      await uploadImage(newFile);
    }
  };

  const handlePreApprovedImage = (selectedImage: PreApprovedImage) => {
    const { name, url } = selectedImage;
    setImage({ name, link: url });
    setImageKey(url);
    append({ name, link: url });
    setIsPreApprovedImage(true);
    setShowPreApprovedImages(false);
  };

  return (
    <StyledImageCard onDragOver={event => event.preventDefault()} onDrop={handleDrop} data-testid={'image-card'}>
      <div className="card">
        {image?.link && !uploadFileLoader ? (
          <>
            <img
              title={image?.name}
              src={
                isPreApprovedImage
                  ? `${image.link}${getPropertyImageUrlParams(PROPERTY_IMAGE_DIMENSIONS.REGULAR_MOBILE_IMAGE_WIDTH)}`
                  : image.link
              }
              alt={image?.name}
              className="card-image"
              loading="eager"
              crossOrigin="anonymous"
            />
            <Button
              className="icon-clear icon-l remove-card"
              ariaLabel={removeAriaLabel}
              callback={handleImageDeletion}
            />
          </>
        ) : (
          <StyledImageCardShell>
            <Text
              element={Types.tags.paragraph}
              copyText={`${placeholder} ${index + 1}`}
              fontSize={Types.size.medium}
              customClass="mb-1"
            />
            {uploadFileLoader || deleteFileLoader ? (
              <div className="text-center mb-5">
                <div className="m-spinner-m" />
              </div>
            ) : (
              <>
                <p className="mb-2 text-center">
                  <Link linkHref="#" linkClassName="t-font-m shell-subtitle" callback={selectImage}>
                    {chooseImage}
                  </Link>
                  <Text
                    element={Types.tags.span}
                    copyText={` ${dragHere}`}
                    fontSize={Types.size.medium}
                    customClass={'t-font-m'}
                  />
                </p>

                {enableImageCropper && (
                  <>
                    <Text
                      element={Types.tags.paragraph}
                      copyText={or}
                      fontSize={Types.size.medium}
                      customClass={'t-font-m mb-2'}
                    />

                    {Object.values(preApprovedPropertyImages.propertyImages).flat().length ? (
                      <>
                        <Link
                          linkHref="#"
                          linkClassName="t-font-m shell-subtitle text-center"
                          callback={e => {
                            e.preventDefault();
                            setShowPreApprovedImages(true);
                          }}
                        >
                          {chooseFromHotelImages}
                        </Link>
                        {showPreApprovedImages && (
                          <PreApprovedImages
                            show={showPreApprovedImages}
                            label={approvedImagesPopUp}
                            onConfirm={image => handlePreApprovedImage(image)}
                            onClose={() => setShowPreApprovedImages(false)}
                            index={index}
                          />
                        )}
                      </>
                    ) : (
                      <Text
                        element={Types.tags.paragraph}
                        copyText={addHotelToSeeImages}
                        fontSize={Types.size.medium}
                        customClass={'t-font-m text-center'}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </StyledImageCardShell>
        )}
        <input
          type="file"
          ref={fileInputRef}
          aria-label={`${chooseImage} ${dragHere}`}
          className="d-none"
          {...{ accept: RESLINK_ALLOWED_IMAGE_TYPES.join(',') }}
          data-testid={'image-input'}
          onChange={handleImageChange}
        />
      </div>
      {enableImageCropper && image?.link && (
        <div className="d-flex justify-content-end m-2">
          <Button
            className="m-button-primary m-button-s"
            callback={() => setShowCropper(true)}
            buttonCopy={crop}
            ariaLabel={cropAriaLabel}
          />
          {showCropper && (
            <ImageCropper
              label={imageCropperPopUp}
              show={showCropper}
              image={image}
              onClose={() => setShowCropper(false)}
              onConfirm={croppedImage => uploadCroppedImage(croppedImage)}
            />
          )}
        </div>
      )}
    </StyledImageCard>
  );
};
